import { useCallback, useEffect, useRef, useState } from "react";

const CENTER = 0;
const OFF_RIGHT = 100;
const OFF_LEFT = -100;

/** @typedef {"right" | "left"} BannerSlideEntrySide */

const sidesForNext = ({ toIndex, getEntrySideForNext }) =>
  getEntrySideForNext(toIndex) === "right"
    ? { incoming: OFF_RIGHT, outgoing: OFF_LEFT }
    : { incoming: OFF_LEFT, outgoing: OFF_RIGHT };

const sidesForPrev = ({ fromIndex, getEntrySideForNext }) =>
  getEntrySideForNext(fromIndex) === "right"
    ? { incoming: OFF_LEFT, outgoing: OFF_RIGHT }
    : { incoming: OFF_RIGHT, outgoing: OFF_LEFT };

const idleXPositions = ({ activeIndex, slideCount }) =>
  Array.from({ length: slideCount }, (_, i) =>
    i === activeIndex ? CENTER : OFF_RIGHT
  );

/**
 * Horizontal slide positions for stacked banner messages (percent of slide width).
 *
 * @param {object} params
 * @param {number} params.slideCount
 * @param {(slideIndex: number) => BannerSlideEntrySide} params.getEntrySideForNext
 * @param {number | undefined} params.autoAdvanceMs
 * @param {boolean} params.enabled
 * @param {number} [params.transitionMs=350]
 */
export const useBannerSlideCarousel = ({
  slideCount,
  getEntrySideForNext,
  autoAdvanceMs,
  enabled,
  transitionMs = 350,
}) => {
  const activeRef = useRef(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [xPercentBySlide, setXPercentBySlide] = useState(() =>
    idleXPositions({ activeIndex: 0, slideCount })
  );
  const [suppressTransition, setSuppressTransition] = useState(false);

  useEffect(() => {
    activeRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    setActiveIndex(0);
    setXPercentBySlide(idleXPositions({ activeIndex: 0, slideCount }));
  }, [slideCount]);

  const applyPairTransition = useCallback(
    ({ from, to, incoming, outgoing }) => {
      setXPercentBySlide((prev) => {
        if (prev[to] !== incoming) {
          setSuppressTransition(true);
          const snap = [...prev];
          snap[to] = incoming;
          requestAnimationFrame(() => {
            setSuppressTransition(false);
            setXPercentBySlide((cur) => {
              const next = [...cur];
              next[from] = outgoing;
              next[to] = CENTER;
              return next;
            });
            setActiveIndex(to);
          });
          return snap;
        }
        const next = [...prev];
        next[from] = outgoing;
        next[to] = CENTER;
        setActiveIndex(to);
        return next;
      });
    },
    []
  );

  const goNext = useCallback(() => {
    if (slideCount < 2) return;
    const from = activeRef.current;
    const to = (from + 1) % slideCount;
    const { incoming, outgoing } = sidesForNext({
      toIndex: to,
      getEntrySideForNext,
    });
    applyPairTransition({ from, to, incoming, outgoing });
  }, [slideCount, getEntrySideForNext, applyPairTransition]);

  const goPrev = useCallback(() => {
    if (slideCount < 2) return;
    const from = activeRef.current;
    const to = (from - 1 + slideCount) % slideCount;
    const { incoming, outgoing } = sidesForPrev({
      fromIndex: from,
      getEntrySideForNext,
    });
    applyPairTransition({ from, to, incoming, outgoing });
  }, [slideCount, getEntrySideForNext, applyPairTransition]);

  useEffect(() => {
    if (!enabled || slideCount === 0) return undefined;

    const timeoutId = setTimeout(() => {
      setSuppressTransition(true);
      setXPercentBySlide(idleXPositions({ activeIndex, slideCount }));
      requestAnimationFrame(() => setSuppressTransition(false));
    }, transitionMs);

    return () => clearTimeout(timeoutId);
  }, [activeIndex, enabled, slideCount, transitionMs]);

  useEffect(() => {
    if (!enabled || !autoAdvanceMs || slideCount < 2) return undefined;

    const intervalId = setInterval(goNext, autoAdvanceMs);
    return () => clearInterval(intervalId);
  }, [autoAdvanceMs, enabled, goNext, slideCount]);

  return {
    activeIndex,
    goNext,
    goPrev,
    suppressTransition,
    xPercentBySlide,
    transitionCss: `transform ${transitionMs / 1000}s ease-out`,
  };
};
